/**
 * Client-side contact scrambling utility
 * 
 * This utility provides preview functionality for contact scrambling
 * to show users what their scrambled contact information will look like
 * when submitted to lenders. Mirrors the server-side logic in app/server/utils/contactScramble.js
 */

/**
 * Scrambles a phone number by replacing the last 3 digits with random numbers
 * 
 * @param {string} phoneNumber - The phone number to scramble
 * @returns {string} The scrambled phone number
 */
export const scramblePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return phoneNumber;
  }
  
  // Remove all non-digits
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If not enough digits, return as is
  if (digitsOnly.length < 3) {
    return phoneNumber;
  }
  
  // Generate 3 random digits
  const randomDigits = Array.from({ length: 3 }, () => Math.floor(Math.random() * 10)).join('');
  
  // Replace last 3 digits
  const scrambled = digitsOnly.slice(0, -3) + randomDigits;
  
  // Format the scrambled number to match the original format if possible
  return formatScrambledPhoneNumber(phoneNumber, scrambled);
};

/**
 * Attempts to format the scrambled phone number to match the original format
 * 
 * @param {string} original - The original formatted phone number
 * @param {string} scrambled - The scrambled digits-only phone number
 * @returns {string} The formatted scrambled phone number
 */
const formatScrambledPhoneNumber = (original, scrambled) => {
  // If original has common formats like (xxx) xxx-xxxx, try to preserve
  if (original.includes('(') && original.includes(')')) {
    // Handle (xxx) xxx-xxxx format
    const parts = original.split(')');
    if (parts.length === 2) {
      const areaCode = parts[0].replace(/\D/g, '');
      if (scrambled.length >= 10) {
        // For US number: (area code) prefix-line
        return `(${scrambled.slice(0, 3)}) ${scrambled.slice(3, 6)}-${scrambled.slice(6, 10)}`;
      }
    }
  } else if (original.includes('-')) {
    // Handle xxx-xxxx or xxx-xxx-xxxx format
    const parts = original.split('-');
    if (parts.length === 2 && scrambled.length >= 7) {
      // For 7-digit number: prefix-line
      return `${scrambled.slice(0, 3)}-${scrambled.slice(3)}`;
    } else if (parts.length === 3 && scrambled.length >= 10) {
      // For 10-digit number: area-prefix-line
      return `${scrambled.slice(0, 3)}-${scrambled.slice(3, 6)}-${scrambled.slice(6, 10)}`;
    }
  }
  
  // Default: return digits only
  return scrambled;
};

/**
 * Scrambles an email address by adding birth year or random digits before the @ symbol
 * 
 * @param {string} email - The email address to scramble
 * @param {string|null} birthYear - The contact's birth year if available
 * @returns {string} The scrambled email address
 */
export const scrambleEmail = (email, birthYear = null) => {
  if (!email || typeof email !== 'string' || !email.includes('@')) {
    return email;
  }
  
  // Split into local part and domain
  const [localPart, ...domainParts] = email.split('@');
  const domain = domainParts.join('@'); // Rejoin in case there were multiple @ symbols
  
  // Decide whether to use birth year or random digits (50/50 chance)
  let suffix;
  if (birthYear && Math.random() > 0.5) {
    // Use birth year
    suffix = birthYear;
  } else {
    // Use 2 random digits
    suffix = Math.floor(Math.random() * 100).toString().padStart(2, '0');
  }
  
  // Create the scrambled email
  const scrambled = `${localPart}${suffix}@${domain}`;
  
  return scrambled;
};

/**
 * Gets the birth year from business data for email scrambling
 * 
 * @param {Object} businessData - The business data object
 * @returns {string|null} The birth year if found, null otherwise
 */
export const getBirthYearFromBusinessData = (businessData) => {
  if (!businessData || typeof businessData !== 'object') {
    return null;
  }
  
  // Try to find birth year in various possible locations
  const dateFields = [
    'DateOfBirth',
    'ContactDateOfBirth', 
    'Contact2DateOfBirth'
  ];
  
  for (const field of dateFields) {
    if (businessData[field]) {
      const dateStr = businessData[field];
      try {
        // Parse date format (expecting yyyy-mm-dd)
        if (typeof dateStr === 'string' && dateStr.includes('-')) {
          return dateStr.split('-')[0];
        }
      } catch (e) {
        console.log('Could not parse birth year from date:', dateStr);
      }
    }
  }
  
  return null;
};

/**
 * Preview scrambled contact information
 * 
 * @param {string} phoneNumber - The phone number to preview scrambling for
 * @param {string} email - The email to preview scrambling for  
 * @param {Object} businessData - The business data object (for birth year)
 * @returns {Object} Object containing scrambled phone and email
 */
export const previewScrambledContact = (phoneNumber, email, businessData = {}) => {
  const birthYear = getBirthYearFromBusinessData(businessData);
  
  return {
    phone: phoneNumber ? scramblePhoneNumber(phoneNumber) : '',
    email: email ? scrambleEmail(email, birthYear) : ''
  };
};
